<template>
  <div class="due-date-range-picker">
    <div class="due-date-range-picker__inputs">
      <input
        :value="displayValue[0]"
        autocomplete="off"
        class="el-range-input"
        @change="handleInputChange(0, $event)"
      >
      <input
        :value="displayValue[1]"
        autocomplete="off"
        class="el-range-input"
        @change="handleInputChange(1, $event)"
      >
    </div>
    <el-date-picker-panel
      :model-value="rangeDates"
      :unlink-panels="unlinkPanels"
      :border="false"
      type="daterange"
      @update:model-value="handlePick"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import dayjs from '@/helpers/dayjs'

/**
 * An always-open date range calendar, with the start and end dates as editable text above it
 */
export default defineComponent({
  name: 'DueDateRangePicker',

  props: {
    /* [start, end]: anything dayjs reads */
    modelValue: {
      type: Array as PropType<any[]>,
      default: (): any[] => [],
    },
    /* dayjs format of the dates in the text inputs and the selected-range event */
    format: {
      type: String,
      default: 'MMMM DD, YYYY',
    },
    unlinkPanels: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['selected-range', 'update:modelValue'],

  computed: {
    rangeDates(): Date[] {
      const dates = (this.modelValue || []).map(value => dayjs(value))
      return dates.length === 2 && dates.every(date => date.isValid())
        ? dates.map(date => date.toDate())
        : []
    },
    displayValue(): string[] {
      return this.rangeDates.length
        ? this.rangeDates.map(date => dayjs(date).format(this.format))
        : ['', '']
    },
  },

  watch: {
    displayValue(newValue: string[], oldValue: string[]) {
      if (newValue.join() !== oldValue.join()) {
        this.$emit('selected-range', newValue)
      }
    },
  },

  methods: {
    handlePick(range: Date[]) {
      this.$emit('update:modelValue', range)
    },

    /* A typed date replaces that end of the range, if it reads as a date */
    handleInputChange(index: number, event: Event) {
      const input = event.target as HTMLInputElement
      const date = dayjs(input.value)
      if (!date.isValid() || !this.rangeDates.length) {
        input.value = this.displayValue[index]
        return
      }
      const range = [...this.rangeDates]
      range[index] = date.toDate()
      this.$emit('update:modelValue', range[0] <= range[1] ? range : [range[1], range[0]])
    },
  },
})
</script>

<style lang="scss">
.due-date-range-picker {
  padding: 10px;
  background-color: var(--color-background-header);

  &__inputs {
    display: flex;
    padding-bottom: 18px;

    .el-range-input {
      width: 230px !important;
      height: 36px !important;
      padding: 0 12px;
      color: var(--color-text-secondary);
      text-align: left;
      background-color: var(--color-general-white);
      border: 1px solid $light-blue-grey-two;
      border-radius: 8px;
      outline: none;

      &:first-of-type {
        margin-right: 55px;
      }
    }
  }

  .el-picker-panel.el-date-range-picker {
    max-width: 100%;
    padding: 0 5px !important;
    margin-top: 4px;
    background-color: transparent;
    border: none;
    box-shadow: none;
  }
}
</style>
